import React, { useState, useContext } from 'react';
import { AuthContext } from '../App';
import { Mail, X, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * EmailVerificationBanner - Shows a persistent banner for unverified users
 * Prompts them to verify their email to unlock all features
 */
const EmailVerificationBanner = () => {
  const { user, token } = useContext(AuthContext);
  const [dismissed, setDismissed] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [error, setError] = useState('');

  // Don't show if:
  // - No user logged in
  // - User has verified their email
  // - Banner was dismissed this session
  if (!user || user.is_email_confirmed || dismissed) {
    return null;
  }

  const handleResendEmail = async () => {
    setResending(true);
    setError('');
    setResendSuccess(false);

    try {
      const response = await fetch(`${API}/api/resend-confirmation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setResendSuccess(true);
        // Clear success message after 5 seconds
        setTimeout(() => setResendSuccess(false), 5000);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to resend email');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div 
      className="fixed top-[60px] left-0 right-0 z-[9997] bg-gradient-to-r from-amber-500/95 to-orange-500/95 backdrop-blur-sm shadow-lg"
      role="alert"
      aria-live="polite"
      data-testid="email-verification-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
          {/* Message */}
          <div className="flex items-center gap-3 text-white">
            <Mail className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm font-medium">
              <span className="hidden sm:inline">Please verify your email to unlock all features like messaging, video calls, and speed dating.</span>
              <span className="sm:hidden">Verify your email to unlock all features.</span>
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            {resendSuccess ? (
              <span className="text-white text-sm font-medium bg-green-600/50 px-3 py-1 rounded-full">
                ✓ Email sent!
              </span>
            ) : (
              <button
                onClick={handleResendEmail}
                disabled={resending}
                className="flex items-center gap-2 px-4 py-1.5 bg-white/20 hover:bg-white/30 text-white text-sm font-medium rounded-full transition-all disabled:opacity-50"
                data-testid="resend-verification-btn"
              >
                <RefreshCw className={`w-4 h-4 ${resending ? 'animate-spin' : ''}`} />
                {resending ? 'Sending...' : 'Resend Email'}
              </button>
            )}

            {/* Dismiss button */}
            <button
              onClick={() => setDismissed(true)}
              className="p-1.5 hover:bg-white/20 rounded-full transition-all text-white/80 hover:text-white"
              aria-label="Dismiss banner"
              data-testid="dismiss-verification-banner"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <p className="text-white/90 text-xs mt-2 text-center sm:text-left">
            {error}
          </p>
        )}
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
